<template>
  <div class="relative flex items-center justify-center w-full h-full" data-testid="viewer-image-backdrop" @click.self="openModal()">
    <img
      class="outline-none cursor-pointer max-w-full focus-visible:ring focus-visible:ring-blue-500/50"
      :style="{ maxHeight: maxImageHeight }"
      ref="imageRef"
      data-testid="viewer-content-image"
      @load="onImageLoad"
      @click="openModal()"
      @keyup.enter="openModal()"
      :src="props.src"
      :alt="alt"
      tabindex="0"
    />
    <div
      v-if="showExpandHint"
      class="absolute bottom-3 right-3 z-10 rounded-md bg-black/50 p-2 text-white pointer-events-none"
      data-testid="viewer-image-expand-hint"
    >
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.5 1.5H10.5V4.5M4.5 10.5H1.5V7.5M10.5 1.5L7 5M1.5 10.5L5 7"
          stroke="white"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
    <TextSelectOverlay v-if="hasOcrData" :image-bounds="imageBounds" />
  </div>
</template>
<script setup lang="ts">
import { ref, computed, Ref, onMounted, onUnmounted, nextTick } from 'vue';
import { usePhotoSwipe } from './../../composables/usePhotoSwipe';
import { useImageBounds } from './../../composables/useImageBounds';
import { TrackEvents, useTrack } from '@/composables/useTrack';
import { useViewerItemStore } from './../../store/useViewerItemStore';
import { useArtifactsStore } from '../../store/useArtifactsStore';
import TextSelectOverlay from '../ocr/TextSelectOverlay.vue';

const props = defineProps<{
  src: string;
  alt?: string;
}>();

const viewerItemStore = useViewerItemStore();
const artifactsStore = useArtifactsStore();
const hasOcrData = computed(() => artifactsStore.isOcrDone && artifactsStore.hasOcrData);

const { init, open } = usePhotoSwipe(props.src);
const imageRef: Ref<HTMLImageElement | null> = ref(null);
const { track } = useTrack();
const { imageBounds, updateImageBounds } = useImageBounds(imageRef, hasOcrData);

const maxImageHeight = ref('80vh');
const showExpandHint = ref(false);

const imageRenderedHeightBeforeMaxHeight = (img: HTMLImageElement) => {
  const containerWidth = img.parentElement?.getBoundingClientRect().width || img.getBoundingClientRect().width || img.naturalWidth;
  const widthScale = img.naturalWidth > 0 ? Math.min(1, containerWidth / img.naturalWidth) : 1;

  return img.naturalHeight * widthScale;
};

const updateExpandHint = () => {
  const img = imageRef.value;
  if (!img) {
    showExpandHint.value = false;
    return;
  }

  const maxHeight = Number.parseInt(maxImageHeight.value, 10);
  showExpandHint.value = Number.isFinite(maxHeight) && imageRenderedHeightBeforeMaxHeight(img) > maxHeight;
};

const updateMaxHeight = () => {
  const img = imageRef.value;
  if (!img) {
    return;
  }
  const available = img.parentElement?.getBoundingClientRect().height ?? 0;
  if (available > 100) {
    maxImageHeight.value = `${Math.floor(available)}px`;
  }
  updateExpandHint();
};

let resizeRafId: number | undefined;
const onResize = () => {
  if (resizeRafId !== undefined) {
    cancelAnimationFrame(resizeRafId);
  }
  resizeRafId = requestAnimationFrame(updateMaxHeight);
};

onMounted(async () => {
  await nextTick();
  updateMaxHeight();
  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  if (resizeRafId !== undefined) {
    cancelAnimationFrame(resizeRafId);
  }
});

// initialize lightbox after image is loaded - need width/height of the image
const onImageLoad = async () => {
  const width = imageRef.value ? imageRef.value.naturalWidth : undefined;
  const height = imageRef.value ? imageRef.value.naturalHeight : undefined;

  init(width, height);
  updateImageBounds();
  await nextTick();
  updateMaxHeight();
};

const openModal = () => {
  open();
  track(TrackEvents.VIEWER_ITEM_ZOOM_CLICK, { viewer: 'copernicus', hashid: viewerItemStore.item?.id });
};
</script>
<style lang="scss">
.pswp--custom-bg {
  --pswp-bg: rgb(56, 69, 90);
  --pswp-icon-color: #f1f5f9;
  --pswp-icon-color-secondary: #141c31;
  --pswp-icon-stroke-color: transparent;
}

.pswp__bg {
  background: rgb(56, 69, 90) !important;
}

.pswp {
  max-width: 100vw;
}

body .pswp__button--close,
body .pswp__button--zoom {
  position: fixed !important;
  top: calc(10px + env(safe-area-inset-top)) !important;
  z-index: 11 !important;
  width: 50px !important;
  height: 50px !important;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 8px;
  background-color: rgba(20, 28, 49, 0.4);
}

body .pswp__button--close:hover,
body .pswp__button--zoom:hover {
  background-color: rgba(20, 28, 49, 1);
}

body .pswp__button--close {
  right: calc(12px + env(safe-area-inset-right)) !important;
}

body .pswp__button--zoom {
  right: calc(70px + env(safe-area-inset-right)) !important;
}

body .pswp__button--close .pswp__icn,
body .pswp__button--zoom .pswp__icn {
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%);
}
</style>
